/**
 * Iter 235c — Carte alertes "score quiz faible" pour le dashboard formateur.
 *
 * Affiche les apprenants ayant complété le questionnaire de positionnement
 * avec un score < 40 (niveau Débutant), avec :
 *  - Nom + session + date démarrage
 *  - Score + niveau
 *  - Recommandations IA pour adapter le démarrage
 *  - Bouton "Marquer comme lu"
 *
 * Utilisable dans :
 *  - Dashboard formateur (existing)
 *  - Page /admin/formateur-alerts (à créer)
 */
import { useEffect, useState, useCallback } from "react";
import { tr } from "../../i18n/tr";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, AlertTriangle, CheckCircle2, X, Sparkles, Calendar, User } from "lucide-react";
import { toast } from "sonner";

export default function LowScoreAlertCard({ limit = 5, showAll = false, className = "" }) {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState({ alerts: [], unread_count: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosAuth().get("/welcome/formateur-alerts");
      setData(r.data);
    } catch (e) {
      // Non bloquant
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { reload(); }, [reload]);

  const markRead = async (alertId) => {
    try {
      await axiosAuth().post(`/welcome/formateur-alerts/${alertId}/mark-read`);
      reload();
    } catch (e) {
      toast.error( tr("lms.erreur_fe7d", "Erreur"));
    }
  };

  if (loading) return null;
  if (data.alerts.length === 0) return null;

  const visible = showAll ? data.alerts : data.alerts.filter((a) => !a.is_read).slice(0, limit);
  if (visible.length === 0) return null;

  return (
    <div className={`bg-amber-500/5 border border-amber-500/30 rounded-xl p-5 ${className}`} data-testid="low-score-alerts">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-200">{tr("lms.apprenants_a_adapter_d10c", "Apprenants à adapter")}</h3>
            <p className="text-[11px] text-slate-400">
              {data.unread_count > 0 ? `${data.unread_count} alerte(s) non lue(s) — Qualiopi indicateur 12` : tr("lms.tous_les_apprenants_ont_c053", "Tous les apprenants ont un niveau adapté")}
            </p>
          </div>
        </div>
        {data.unread_count > 0 && (
          <span className="px-2 py-0.5 bg-rose-500 text-white text-xs font-bold rounded-full">{data.unread_count}</span>
        )}
      </div>

      <div className="space-y-3">
        {visible.map((a) => (
          <AlertItem key={a.alert_id} alert={a} onMarkRead={markRead} />
        ))}
      </div>

      {!showAll && data.alerts.length > limit && (
        <div className="text-center mt-3">
          <a href="/admin/formateur-alerts" className="text-xs text-amber-300 hover:text-amber-200">
            Voir toutes les alertes ({data.alerts.length}) →
          </a>
        </div>
      )}
    </div>
  );
}


function AlertItem({ alert, onMarkRead }) {
  const a = alert;
  const startDate = a.session_start_date ? new Date(a.session_start_date).toLocaleDateString("fr-FR") : "—";
  return (
    <div className={`bg-[#0b0811] border rounded-lg p-4 ${a.is_read ? "border-white/5 opacity-60" : "border-amber-500/30"}`} data-testid={`alert-${a.alert_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-sm font-bold text-white">{a.learner_name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300">{a.global_level}</span>
            <span className="text-[10px] text-slate-500 font-mono">{a.global_score}/100</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <Calendar className="w-3 h-3" />
            <span>{a.session_name}</span>
            <span className="text-slate-600">•</span>
            <span>démarre le {startDate}</span>
          </div>
        </div>
        {!a.is_read && (
          <button onClick={() => onMarkRead(a.alert_id)} className="p-1 hover:bg-white/5 rounded text-slate-400 shrink-0" title={tr("lms.marquer_comme_lu_9c7c", "Marquer comme lu")} data-testid={`alert-mark-read-${a.alert_id}`}>
            <X className="w-4 h-4" />
          </button>
        )}
        {a.is_read && (
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
        )}
      </div>

      {a.ai_summary && (
        <div className="mt-2 pl-5 border-l-2 border-cyan-500/30">
          <div className="flex items-center gap-1 text-[10px] uppercase text-cyan-400 font-bold mb-1">
            <Sparkles className="w-3 h-3" /> {tr("lms.recommandation_ia_5b21", "Recommandation IA")}
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">{a.ai_summary}</p>
        </div>
      )}

      {a.recommended_pace && (
        <div className="mt-2 inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-2 py-1 text-[11px]">
          <span className="text-slate-400">{tr("lms.rythme_conseille_e546", "Rythme conseillé :")} </span>
          <span className="text-cyan-300 font-medium capitalize">{a.recommended_pace}</span>
        </div>
      )}

      {(a.personalization_advice || []).length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] text-slate-400 hover:text-slate-200 cursor-pointer">
            {a.personalization_advice.length} conseil(s) pédagogique(s)
          </summary>
          <ul className="mt-1.5 ml-3 space-y-1">
            {a.personalization_advice.map((c, i) => (
              <li key={i} className="text-[11px] text-slate-300">• {c}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
